import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeModalWithResult, setModalState, modalViewsConst } from '../../store/modalSlice';
import { updateSchedule } from '../../store/agentScheduleSlice';
import { ReactComponent as SettingsIcon } from '../../assets/icons/user-settings-new.svg';
import ModalBase from './ModalBase';
import { parseRuntime, computeNextRunFromCron } from '../../utils/scheduleFrequency';
import type { RootState, AppDispatch } from '../../store';
import type { AgentSchedule } from '../../services/scheduleService';

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

interface ManageSchedulesModalProps {
  onClose: (result?: any) => void;
}

const ManageSchedulesModal: React.FC<ManageSchedulesModalProps> = ({ onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const allSchedules = useSelector((state: RootState) => state.agentSchedule.schedules);
  const properties = useSelector((state: RootState) => state.modalState.properties);
  const projectId: string | null = properties?.projectId ?? null;
  // Scope to match the ScheduleHistory list: a project shows only its own agents, and the non-project (AI Analyst) view shows only non-project agents.
  const schedules = allSchedules.filter((s) =>
    projectId ? s.projectId === projectId : !s.projectId
  );
  // Track which schedules have an update in flight (toggle or Run Once) so each row disables independently without blocking others
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());
  // Surface toggle failures inline since the store silently reverts on rejection
  const [toggleError, setToggleError] = useState<string | null>(null);

  const handleClose = () => {
    dispatch(closeModalWithResult(null));
    onClose();
  };

  const handleToggleActive = async (schedule: AgentSchedule) => {
    if (togglingIds.has(schedule.id)) return;
    // PAUSED stops the poller; keep storing PAUSED but label it "Inactive" in the UI
    const nextStatus = schedule.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    setToggleError(null);
    setTogglingIds((prev) => new Set(prev).add(schedule.id));
    try {
      // Reactivating resumes at the next cron occurrence rather than leaving a stale (past-due) nextRunAt that the poller would fire immediately — matters for schedules auto-paused by the quota guard rails. Also clears the auto-pause error so its explanation doesn't linger after resuming.
      await dispatch(updateSchedule({
        id: schedule.id,
        status: nextStatus,
        ...(nextStatus === 'ACTIVE' && {
          nextRunAt: computeNextRunFromCron(schedule.cronExpression, schedule.timezone),
          error: null,
        }),
      })).unwrap();
    } catch {
      setToggleError('Couldn’t update schedule. Please try again.');
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(schedule.id);
        return next;
      });
    }
  };

  const handleRunNow = async (schedule: AgentSchedule) => {
    if (togglingIds.has(schedule.id)) return;
    setToggleError(null);
    setTogglingIds((prev) => new Set(prev).add(schedule.id));
    try {
      // Set nextRunAt to now so the poller picks it up on the next cycle (within 5 min)
      await dispatch(updateSchedule({
        id: schedule.id,
        status: 'ACTIVE',
        nextRunAt: new Date().toISOString(),
      })).unwrap();
    } catch {
      setToggleError('Couldn’t run agent now. Please try again.');
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(schedule.id);
        return next;
      });
    }
  };

  const handleEditClick = (schedule: AgentSchedule) => {
    dispatch(setModalState({
      modalOpen: true,
      modalView: modalViewsConst.editSchedule,
      properties: { schedule, __modalSize: 'md' },
    }));
  };

  const handleDeleteClick = (schedule: AgentSchedule) => {
    dispatch(setModalState({
      modalOpen: true,
      modalView: modalViewsConst.generalDelete,
      properties: {
        __modalSize: 'sm',
        title: 'Delete Agent',
        itemName: schedule.title,
        itemId: schedule.id,
        itemType: 'schedule',
        cancelView: modalViewsConst.manageSchedules,
        // Re-open Manage Schedules as a ModalBase modal (md width), preserving project scope.
        cancelProperties: projectId ? { projectId, __modalSize: 'md' } : { __modalSize: 'md' },
        confirmView: modalViewsConst.manageSchedules,
        confirmProperties: projectId ? { projectId, __modalSize: 'md' } : { __modalSize: 'md' },
      },
    }));
  };

  return (
    <ModalBase
      title="Manage Agents"
      icon={<SettingsIcon className="h-5 w-5" />}
      onClose={handleClose}
      footer={
        <button onClick={handleClose} className="eai-btn-outline">
          Close
        </button>
      }
    >
      <div>
        {schedules.length === 0 && (
          <p className="eai-description-sm py-8 text-center">No agents found.</p>
        )}

        {toggleError && (
          <p className="mb-3 font-inter text-eai-body-sm text-eai-danger-text">{toggleError}</p>
        )}

        <div className="flex flex-col gap-4">
          {schedules.map((schedule) => {
            const frequency = parseRuntime(schedule.runtime).type;
            const isActive = schedule.status === 'ACTIVE';

            return (
              <div key={schedule.id} className="eai-card-interactive group flex flex-col p-4">
                {/* Title + active switch. The switch wraps below the title on a phone, where the two together overrun the card's measure. */}
                <div className="mb-2 flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5">
                  <span className="eai-card-title min-w-0 flex-1 truncate">{schedule.title}</span>
                  <div className="flex flex-none items-center gap-2">
                    {/* Active/Inactive toggle — flips status between ACTIVE and PAUSED */}
                    <button
                      type="button"
                      role="switch"
                      aria-checked={isActive}
                      aria-label={isActive ? 'Set agent inactive' : 'Set agent active'}
                      disabled={togglingIds.has(schedule.id)}
                      onClick={() => handleToggleActive(schedule)}
                      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
                        isActive ? 'bg-eai-cta-primary' : 'bg-eai-border-hover'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          isActive ? 'translate-x-[18px]' : 'translate-x-0.5'
                        }`}
                      />
                    </button>
                    <span
                      className={`font-outfit text-eai-body-xs ${
                        isActive ? 'text-eai-primary-accent' : 'text-eai-ink-subtle'
                      }`}
                    >
                      {isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                {/* Cadence pill + run count */}
                <div className="mb-2 flex items-center gap-3">
                  <span className="eai-tag-pill-sm transition-colors group-hover:bg-eai-checklist-lt group-hover:text-eai-cta-primary">
                    {frequency}
                  </span>
                  <span className="font-inter text-eai-body-xs text-eai-ink-subtle">
                    <span className="font-medium">Times run:</span> {schedule.runCount || 0}
                  </span>
                </div>

                {/* Error */}
                {schedule.error && (
                  <div className="mb-2 rounded-eai-card border border-eai-danger-border bg-eai-danger-surface px-2.5 py-1.5">
                    <span className="font-inter text-eai-body-xs text-eai-danger-text">{schedule.error}</span>
                  </div>
                )}

                {/* Last / Next — facts, so they stay flat */}
                <div className="mt-2 flex flex-col gap-0.5">
                  <span className="font-inter text-eai-body-xs text-eai-ink-soft">
                    <span className="font-medium text-eai-ink-subtle">Last:</span> {formatDate(schedule.lastRunAt)}
                  </span>
                  <span className="font-inter text-eai-body-xs text-eai-ink-soft">
                    <span className="font-medium text-eai-ink-subtle">Next:</span> {formatDate(schedule.nextRunAt)}
                  </span>
                </div>

                {/* Divider + actions — all three off the same xs rung so the row reads as one ladder */}
                <div className="mt-3 flex items-center gap-2 border-t border-eai-border pt-2.5">
                  <button
                    onClick={() => handleRunNow(schedule)}
                    disabled={togglingIds.has(schedule.id)}
                    className="eai-btn-cta-xs"
                  >
                    Run once
                  </button>
                  <button onClick={() => handleEditClick(schedule)} className="eai-btn-outline-xs">
                    Edit
                  </button>
                  <button onClick={() => handleDeleteClick(schedule)} className="eai-btn-danger-xs ml-auto">
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </ModalBase>
  );
};

export default ManageSchedulesModal;
